import React from 'react';
import { GitBranch } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Button } from './ui/button';
import { applyAlpha } from '../lib/colorUtils';

/**
 * BranchIndicator - Shows current branch position in a conversation tree
 * @param {Object} props
 * @param {Object} props.conversation - The current conversation object
 * @param {number} props.branchIndex - Current branch index (1-based)
 * @param {number} props.totalBranches - Total number of branches in this family
 * @param {Function} props.onOpenTree - Callback to open the branch tree modal
 * @param {Object} props.brandVisualTheme - Brand visual theme for styling
 * @param {boolean} props.isBrandTheme - Whether using brand theme
 */
const BranchIndicator = ({
  conversation,
  branchIndex = 1,
  totalBranches = 1,
  onOpenTree,
  brandVisualTheme,
  isBrandTheme = false,
}) => {
  const palette = brandVisualTheme?.palette || {};
  const primaryColor = palette.primary || '#1E8997';
  
  // Don't show if there's only one conversation (no branches)
  if (totalBranches <= 1 && !conversation?.parent_conversation_id) {
    return null;
  }

  const isRootBranch = !conversation?.parent_conversation_id;
  const branchDepth = conversation?.branch_depth ?? 0;
  
  const backgroundColor = isBrandTheme 
    ? 'rgba(255, 255, 255, 0.1)' 
    : applyAlpha(primaryColor, 0.1);
  
  const textColor = isBrandTheme 
    ? 'rgba(255, 255, 255, 0.9)' 
    : primaryColor;

  const borderColorStyle = isBrandTheme 
    ? 'rgba(255, 255, 255, 0.2)' 
    : applyAlpha(primaryColor, 0.3);

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpenTree}
            className="h-7 px-2 gap-1.5 rounded-full transition-all hover:scale-105"
            style={{
              backgroundColor,
              color: textColor,
              border: `1px solid ${borderColorStyle}`,
            }}
          >
            <GitBranch className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">
              {branchIndex}/{totalBranches}
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          className="bg-gray-900 text-white text-xs px-3 py-2"
        >
          <div className="space-y-1">
            <p className="font-medium">
              {isRootBranch ? 'Root conversation' : `Branch level ${branchDepth}`}
            </p>
            <p className="opacity-80">
              {totalBranches} conversation{totalBranches !== 1 ? 's' : ''} in this tree
            </p>
            <p className="opacity-60 text-[10px]">Click to view branch tree</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default BranchIndicator;

